import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["style", "className", "suggestedItems", "name", "onChange", "onSearch", "id", "disabled", "hasError", "isClearable", "value", "hideDropdownIndicator", "minChars", "requestDelayMs", "leftAdornmentIcon", "isMulti", "openMenuOnClick", "renderOption", "menuPlacement", "menuPosition", "portalMenu", "valueRenderType", "inputValue", "onInputChange", "noOptionsMessage"];
import React, { useMemo } from "react";
import ReactSelectAsync from "react-select/async";
import useGetCustomComponents from "../hooks/useGetCustomComponents";
import useMenuPositionProps from "../hooks/useMenuPositionProps";
import useTypeaheadChangeHandlers from "../hooks/useTypeaheadChangeHandlers";
import { getSafeInputValue } from "../logic";
import useLoadOptions from "./hooks/useLoadOptions";
import useMemoizedDebouncedSearch from "./hooks/useMemoizedDebounceSearch";
import { jsx as _jsx } from "react/jsx-runtime";
var AsyncSelect = /*#__PURE__*/React.forwardRef(function (_ref, forwardedRef) {
  var style = _ref.style,
    className = _ref.className,
    suggestedItems = _ref.suggestedItems,
    name = _ref.name,
    onChange = _ref.onChange,
    onSearch = _ref.onSearch,
    id = _ref.id,
    disabled = _ref.disabled,
    hasError = _ref.hasError,
    isClearable = _ref.isClearable,
    value = _ref.value,
    _ref$hideDropdownIndi = _ref.hideDropdownIndicator,
    hideDropdownIndicator = _ref$hideDropdownIndi === void 0 ? false : _ref$hideDropdownIndi,
    _ref$minChars = _ref.minChars,
    minChars = _ref$minChars === void 0 ? 0 : _ref$minChars,
    _ref$requestDelayMs = _ref.requestDelayMs,
    requestDelayMs = _ref$requestDelayMs === void 0 ? 250 : _ref$requestDelayMs,
    leftAdornmentIcon = _ref.leftAdornmentIcon,
    _ref$isMulti = _ref.isMulti,
    isMulti = _ref$isMulti === void 0 ? false : _ref$isMulti,
    openMenuOnClick = _ref.openMenuOnClick,
    renderOption = _ref.renderOption,
    _ref$menuPlacement = _ref.menuPlacement,
    menuPlacement = _ref$menuPlacement === void 0 ? "auto" : _ref$menuPlacement,
    _ref$menuPosition = _ref.menuPosition,
    menuPosition = _ref$menuPosition === void 0 ? "absolute" : _ref$menuPosition,
    _ref$portalMenu = _ref.portalMenu,
    portalMenu = _ref$portalMenu === void 0 ? false : _ref$portalMenu,
    _ref$valueRenderType = _ref.valueRenderType,
    valueRenderType = _ref$valueRenderType === void 0 ? "default" : _ref$valueRenderType,
    inputValue = _ref.inputValue,
    _ref$onInputChange = _ref.onInputChange,
    onInputChange = _ref$onInputChange === void 0 ? function () {
      return null;
    } : _ref$onInputChange,
    noOptionsMessage = _ref.noOptionsMessage,
    props = _objectWithoutProperties(_ref, _excluded);
  var disabledOptions = function disabledOptions(option) {
    return option.disabled;
  };
  var _useGetCustomComponen = useGetCustomComponents({
      hideDropdownIndicator: hideDropdownIndicator,
      component: "asyncSelect",
      valueRenderType: valueRenderType
    }),
    customComponents = _useGetCustomComponen.customComponents,
    styles = _useGetCustomComponen.styles;
  var _useMemoizedDebounced = useMemoizedDebouncedSearch({
      requestDelayMs: requestDelayMs,
      onSearch: onSearch
    }),
    memoizedDebouncedSearch = _useMemoizedDebounced.memoizedDebouncedSearch;
  var _useLoadOptions = useLoadOptions(onSearch, minChars, requestDelayMs, memoizedDebouncedSearch),
    loadOptions = _useLoadOptions.loadOptions;
  var menuPositionProps = useMenuPositionProps(menuPlacement, menuPosition, portalMenu);
  var _useTypeaheadChangeHa = useTypeaheadChangeHandlers(onChange, onInputChange, valueRenderType),
    _useTypeaheadChangeHa2 = _slicedToArray(_useTypeaheadChangeHa, 2),
    handleOnChange = _useTypeaheadChangeHa2[0],
    handleInputOnChange = _useTypeaheadChangeHa2[1];
  var safeInputValue = useMemo(function () {
    return getSafeInputValue(value, inputValue, valueRenderType);
  }, [value, inputValue, valueRenderType]);
  return /*#__PURE__*/_jsx(ReactSelectAsync, _extends({
    id: id,
    loadOptions: loadOptions,
    value: value,
    name: name,
    onChange: handleOnChange,
    ref: forwardedRef,
    openMenuOnClick: openMenuOnClick,
    defaultOptions: suggestedItems,
    isDisabled: disabled,
    isOptionDisabled: disabledOptions,
    "aria-invalid": hasError,
    isClearable: isClearable,
    isMulti: isMulti,
    styles: styles,
    icon: leftAdornmentIcon,
    components: customComponents // inner components that have been customised
    ,
    unstyled: true,
    renderOption: renderOption,
    controlShouldRenderValue: valueRenderType !== "typeahead",
    inputValue: safeInputValue,
    onInputChange: handleInputOnChange,
    noOptionsMessage: noOptionsMessage || valueRenderType === "typeahead" ? function () {
      return "No suggestions";
    } : function () {
      return "No options";
    }
  }, menuPositionProps, props));
});
AsyncSelect.displayName = "AsyncSelect";
export default AsyncSelect;